import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import SelectInput from "../shared/Inputs/SelectInput";

import useCustomer from "../../data/useCustomer";
import useWebsites from "../../data/useWebsites";
import LoadingWheel from "../shared/LoadingWheel";

const NONE_OPTION_ID = "";

export default function CustomerMarketplaceWebsiteSelect() {
  const { customer, update } = useCustomer();
  const { websitesQuery, websites } = useWebsites();

  const { register, watch, setValue } = useForm();

  useEffect(() => {
    if (customer.isLoading) return;
    if (!customer.data?.data) return;

    setValue("marketplaceWebsiteId", customer.data.data.marketplaceWebsiteId ?? NONE_OPTION_ID);
  }, [customer.data, customer.isLoading]);

  const onChange = async (websiteId: string) => {
    setValue("marketplaceWebsiteId", websiteId);
    await update.mutateAsync({
      // The API treats an empty GUID as "clear the selection"
      marketplaceWebsiteId: websiteId || "00000000-0000-0000-0000-000000000000",
    });
  };

  if (customer.isLoading || websitesQuery.isLoading) {
    return <LoadingWheel />;
  }

  // With zero or one website there's nothing meaningful to choose: no sites means safe defaults,
  // and a single site is bound automatically by the API. The dropdown only helps when the customer
  // has more than one site and needs to pick which one represents them on marketplace leads.
  if (websites.length <= 1) {
    return null;
  }

  const options = [
    { id: NONE_OPTION_ID, name: "Use safe defaults" },
    ...websites.map((w) => ({ id: w.id, name: w.domain })),
  ];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-medium text-gray-700">
          Website settings to use when you win a marketplace lead
        </span>
        <div className="relative group flex items-center">
          <QuestionMarkCircleIcon
            className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help"
            aria-hidden="true"
          />
          <div
            role="tooltip"
            className="pointer-events-none absolute right-0 top-6 z-30 w-72 rounded-md border border-gray-200 bg-white p-3 text-xs leading-relaxed text-gray-600 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150"
          >
            When you win a lead from a marketplace (e.g. SellAVehicle) rather than your own hosted
            widget, we apply this website&apos;s look and settings &mdash; theme, preferred
            collection dates, hidden pages and so on. Choose &ldquo;Use safe defaults&rdquo; to fall
            back to our neutral defaults instead of any specific site.
          </div>
        </div>
      </div>
      <SelectInput
        label=""
        register={register}
        registerName="marketplaceWebsiteId"
        options={options}
        watch={watch}
        setValue={(_name: string, value: string) => onChange(value)}
      />
      {update.isLoading && <LoadingWheel />}
    </div>
  );
}
